/**
 * ============================================
 * SERVIÇO EXCLUSIVO DO CHAT GERAL
 * ============================================
 * 
 * Este serviço utiliza a RPC exclusiva `rpc_send_general_message`
 * para resolver o erro RLS 42501 sem impactar outros módulos.
 * 
 * NÃO USAR EM:
 * - Document Chat
 * - AI-inline
 * - Outros fluxos de mensagens
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface GeneralMessagePayload {
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  fileId?: string | null;
  requestId?: string | null;
  originUserMessageId?: string | null;
}

/**
 * Salva mensagem usando RPC exclusiva do Chat Geral
 * Garante user_id e valida propriedade da conversa
 * 🔒 IDEMPOTÊNCIA: Suporta request_id para prevenir duplicatas
 */
export async function saveGeneralMessageRPC(
  payload: GeneralMessagePayload & { requestId?: string }
): Promise<any> {
  console.log('📡 [CHAT GERAL] Salvando mensagem via RPC exclusiva:', {
    conversationId: payload.conversationId,
    role: payload.role,
    contentLength: payload.content.length,
    hasFile: !!payload.fileId,
    requestId: payload.requestId
  });

  try {
    // 🔒 Sempre enviar 6 parâmetros explícitos para evitar ambiguidade de overload
    const { data, error } = await supabase.rpc('rpc_send_general_message' as any, {
      p_conversation_id: payload.conversationId,
      p_role: payload.role,
      p_content: payload.content,
      p_file_id: payload.fileId ?? null,
      p_request_id: payload.requestId ?? null,
      p_origin_user_message_id: payload.originUserMessageId ?? null,
    } as any);

    if (error) {
      console.error('❌ [CHAT GERAL] Erro na RPC:', error);
      
      // Erro de propriedade de conversa
      if (error.code === '28000') {
        toast({
          title: "Erro de Permissão",
          description: "Você não tem permissão para enviar mensagens nesta conversa.",
          variant: "destructive",
        });
        throw new Error('Conversa não pertence ao usuário (Chat Geral)');
      }
      
      // Outros erros
      toast({
        title: "Erro ao Salvar Mensagem",
        description: "Não foi possível salvar a mensagem. Tente novamente.",
        variant: "destructive",
      });
      throw new Error(`Erro RPC Chat Geral: ${error.message}`);
    }

    console.log('✅ [CHAT GERAL] Mensagem salva com sucesso via RPC:', data?.id);
    return data;
  } catch (error) {
    console.error('❌ [CHAT GERAL] Exceção ao salvar mensagem:', error);
    throw error;
  }
}
